import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  useCreateBIKEmployee,
  useDeleteBIKEmployee,
  useUpdateBIKEmployee,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetBIKListingEmployeeListParams } from '@nxsys/store/slices/employee/types';
import {
  CreateBIKEmployeeParams,
  UpdateBIKEmployeeParams,
} from '@nxsys/types/api';
import { Icons } from '@nxsys/types/icons';
import { IBIK } from '@nxsys/types/models/BIK';
import { showToast } from '@nxsys/utils/toast';
import { ColumnType } from 'antd/lib/table';
import { toNumber } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnnualBenefitFormValues } from './AnnualBenefitForm';
import CreateAnnualBenefitModal from './CreateAnnualBenefitModal';
import EditAnnualBenefitModal from './EditAnnualBenefitModal';


type Props = {
  employeeId: string;
};

const AnnualBenefit = ({ employeeId }: Props) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();

  const { list, loading, pagination, queryParams } = useSelector(
    (state: AppState) => state.employeeStore.bikListingList,
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      types: ['ANNUAL_BENEFIT'],
    });
  }, []);

  const handleFetchData = (params: GetBIKListingEmployeeListParams) => {
    const newParams = {
      ...params,
      subcontractorId: employeeId,
    };
    dispatch(employeeActions.getBIKListingEmployeeList(newParams));
  };

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnType<IBIK>[] = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="table-actions flex items-center justify-start">
          <li onClick={() => toggleEdit(record)}>
            <Icons.IconEdit />
          </li>
          <li onClick={() => handleDelete(record.id)}>
            <Icons.IconDelete />
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'description',
      title: messages['common.description'],
      dataIndex: 'description',
    },
    {
      key: 'value',
      title: messages['common.value'],
      dataIndex: 'value',
    },
    {
      key: 'eeContribution',
      title: messages['common.employeeContribution'],
      dataIndex: 'eeContribution',
    },
    {
      key: 'totalEeContribution',
      title: messages['common.totalEmployeeContribution'],
      dataIndex: 'totalEeContribution',
    },
    {
      key: 'startDate',
      title: messages['common.startDate'],
      dataIndex: 'startDate',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      key: 'endDate',
      title: messages['common.endDate'],
      dataIndex: 'endDate',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
  ];

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const toggleCreate = () => {
    setOpenCreateModal((prev) => !prev);
  };
  const { run: createBIKEmployee } = useCreateBIKEmployee();
  const handleCreate = async (values: AnnualBenefitFormValues) => {
    const params: CreateBIKEmployeeParams = {
      value: toNumber(values.value),
      startDate: values.startDate ? values.startDate.toISOString() : undefined,
      description: values.description,
      endDate: values.endDate
        ? values.endDate.toISOString()
        : undefined,
      eeContribution: values.eeContribution,
      totalEeContribution: toNumber(values.totalEeContribution),
      subcontractorId: employeeId,
      type: 'ANNUAL_BENEFIT',
    };
    const res = await createBIKEmployee(params);
    if (res.success) {
      showToast({
        type: 'success',
        message: messages['common.addSuccess'],
      });
      toggleCreate();
      refetchData();
    }
  };

  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedData, setSelectedData] = useState<IBIK | null>(null);
  const toggleEdit = (record?: IBIK) => {
    setOpenEditModal((prev) => !prev);
    setSelectedData(record || null);
  };
  const { run: updateBIKEmployee } = useUpdateBIKEmployee();
  const handleUpdate = async (values: AnnualBenefitFormValues) => {
    const params: UpdateBIKEmployeeParams = {
      value: toNumber(values.value),
      startDate: values.startDate ? values.startDate.toISOString() : undefined,
      description: values.description,
      endDate: values.endDate
        ? values.endDate.toISOString()
        : undefined,
      eeContribution: values.eeContribution,
      totalEeContribution: toNumber(values.totalEeContribution),
      subcontractorId: employeeId,
      type: 'ANNUAL_BENEFIT',
    };
    const res = await updateBIKEmployee(selectedData!.id, params);
    if (res.success) {
      showToast({
        type: 'success',
        message: messages['common.updateSuccess'],
      });
      toggleEdit();
      refetchData();
    }
  };

  const { run: deleteBIKEmployee } = useDeleteBIKEmployee();
  const handleDelete = async (id: number) => {
    AppConfirmationDelete.open({
      title: messages['common.delete'],
      paragraph: messages['common.deleteConfirm'],
      onConfirm: async () => {
        const res = await deleteBIKEmployee(id);
        if (res.success) {
          showToast({
            type: 'success',
            message: messages['common.deleteSuccess'],
          });
          refetchData();
        }
      },
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-[16px]">
        <div></div>
        <AppButton
          type="primary"
          name={messages['common.addNew']}
          leftIcon={<PlusOutlined />}
          onClick={toggleCreate}
        />
      </div>
      <AppsContent>
        <AppTableContainer
          data={list || []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1500, y: 700 }}
          titleEmpty={messages['common.noData']}
          columns={columns}
          onChangePagination={onChangePagination}
          noDataType="noDataCreated"
          addNewBtnOnClick={toggleCreate}
          dataTypeName={messages['common.annualBenefit']}
        />
      </AppsContent>
      <CreateAnnualBenefitModal
        isOpen={openCreateModal}
        toggle={toggleCreate}
        onSubmit={handleCreate}
        employeeId={employeeId}
      />
      {selectedData && (
        <EditAnnualBenefitModal
          isOpen={openEditModal}
          toggle={toggleEdit}
          onSubmit={handleUpdate}
          data={selectedData}
        />
      )}
    </div>
  );
};

export default AnnualBenefit;
